import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { IChatMessage } from '@nimbus-global/features/chat/models/chat.model';
import { CompanionSplitScreenCacheService } from '@nimbus-library/features/companion-chat/services/companion-split-screen-cache.service';

export interface CompanionSplitScreenState {
  conversationId: string | null;
  messages: IChatMessage[];
}

@Injectable({ providedIn: 'root' })
export class CompanionSplitScreenService {
  private readonly activeSubject = new BehaviorSubject<boolean>(false);
  private readonly expandRequestedSubject = new Subject<void>();
  private readonly collapseRequestedSubject = new Subject<void>();
  private readonly stateSubject = new BehaviorSubject<CompanionSplitScreenState | null>(null);

  public readonly active$ = this.activeSubject.asObservable();
  public readonly expandRequested$ = this.expandRequestedSubject.asObservable();
  public readonly collapseRequested$ = this.collapseRequestedSubject.asObservable();
  public readonly state$ = this.stateSubject.asObservable();

  public get state(): CompanionSplitScreenState | null {
    return this.stateSubject.value;
  }

  public get isActive(): boolean {
    return this.activeSubject.value;
  }

  public get conversationId(): string | null {
    const session = this.splitScreenCacheService.get();
    return session?.enabled && session.conversationId ? session.conversationId : null;
  }

  public get panelWidthPct(): number | null {
    const session = this.splitScreenCacheService.get();
    return session?.enabled ? (session.widthPct ?? null) : null;
  }

  public constructor(private readonly splitScreenCacheService: CompanionSplitScreenCacheService) {
    this.initialize();
  }

  public requestExpand(): void {
    this.expandRequestedSubject.next();
  }

  public requestCollapse(): void {
    this.collapseRequestedSubject.next();
  }

  public startExpand(state: CompanionSplitScreenState): void {
    this.stateSubject.next(state);
    this.activeSubject.next(false);
  }

  public activate(state: CompanionSplitScreenState): void {
    const session = this.splitScreenCacheService.get();
    this.splitScreenCacheService.set({ conversationId: state.conversationId, enabled: true, widthPct: session?.widthPct });
    this.stateSubject.next(state);
    this.activeSubject.next(true);
  }

  public updateConversationId(conversationId: string): void {
    const session = this.splitScreenCacheService.get();
    if (session?.enabled) {
      this.splitScreenCacheService.set({ ...session, conversationId });
    }
  }

  public setPanelWidthPct(widthPct: number): void {
    const session = this.splitScreenCacheService.get();
    if (session?.enabled) {
      this.splitScreenCacheService.set({ ...session, widthPct });
    }
  }

  public deactivate(): void {
    this.activeSubject.next(false);
    this.stateSubject.next(null);
    this.splitScreenCacheService.clear();
  }

  public clearState(): void {
    this.stateSubject.next(null);
    this.splitScreenCacheService.clear();
  }

  private initialize(): void {
    const session = this.splitScreenCacheService.get();
    if (session?.enabled) {
      this.activeSubject.next(true);
    }
  }
}
